import {
  PPQ,
  ticks as durTicks,
  midiOf,
  spellMidi,
  computeAccidentals,
  beatGroups,
  barCapacity,
  type AccidentalMark,
  type AccidentalInput,
  type Pitch,
} from '@simplenotation/theory';
import type { Score, Track, Beat, Note, Clef, BarInfo, Direction, KeySignature } from '../../model/types';
import { timeSigAt, keySigAt, isFretted } from '../../model/query';
import { instrumentDef } from '../../model/instruments';
import { markQpm } from '../../timeline';
import { XmlWriter, type XmlAttrs } from '../xml';
import { gmProgramOf, xmlSoundOf } from '../sounds';
import { writtenKey, TYPE_NAMES, parseChordName, stepAlterText } from '../common';
import { midiChannels } from '../midi/export';

/** Divisions per quarter note in exported files (= the model's PPQ). */
export const DIVISIONS = PPQ;

/** Octave shift between our written pitch and the exported MusicXML pitch for a clef. */
export function clefXmlShift(clef: Clef): number {
  return clef === 'treble8vb' ? -12 : 0;
}

const DIATONIC_FOR_SEMIS = [0, 1, 1, 2, 2, 3, 3, 4, 5, 5, 6, 6];

const ALTER_NAMES: Record<number, string> = { [-2]: 'flat-flat', [-1]: 'flat', 0: 'natural', 1: 'sharp', 2: 'double-sharp' };

const DIRECTION_WORDS: Record<Direction, string> = {
  DC: 'D.C.',
  DS: 'D.S.',
  'DC al Fine': 'D.C. al Fine',
  'DS al Coda': 'D.S. al Coda',
  'DC al Coda': 'D.C. al Coda',
  Fine: 'Fine',
  Coda: 'Coda',
  Segno: 'Segno',
  ToCoda: 'To Coda',
};

interface FlatBeat {
  bar: number;
  idx: number;
  beat: Beat;
}

interface TrackPrep {
  tieStop: Set<string>;
  /** Origin note id → hammer-on / pull-off. */
  hpStart: Map<string, 'hammer-on' | 'pull-off'>;
  hpStop: Map<string, 'hammer-on' | 'pull-off'>;
  slideStop: Set<string>;
  legatoStop: Set<string>;
  /** Origin note id → destination exists (slide start is only written when it has a destination). */
  slideStart: Set<string>;
  /** Beat id → wedge numbers to stop right after this beat. */
  wedgeStopAfter: Map<string, number[]>;
  /** Before-beat run starts and after-beat run stops. */
  before: Map<string, RunEvent[]>;
  after: Map<string, RunEvent[]>;
}

type RunEvent = { kind: 'letRing'; start: boolean; voice: number } | { kind: 'octave'; start: boolean; voice: number; shift: number };

function flatVoice(track: Track, v: number): FlatBeat[] {
  const out: FlatBeat[] = [];
  track.bars.forEach((tb, bar) => {
    (tb.voices[v] ?? []).forEach((beat, idx) => out.push({ bar, idx, beat }));
  });
  return out;
}

function isRestBeat(b: Beat): boolean {
  return b.rest || b.notes.length === 0;
}

/** Destination note for a legato effect / tie from `n` in the next non-grace beat. */
function destinationNote(n: Note, next: Beat | undefined, samePitch: boolean): Note | undefined {
  if (!next || isRestBeat(next)) return undefined;
  const m = midiOf(n.pitch);
  if (samePitch) return next.notes.find((x) => midiOf(x.pitch) === m);
  if (n.string !== undefined) {
    const s = next.notes.find((x) => x.string === n.string);
    if (s) return s;
  }
  if (next.notes.length === 1) return next.notes[0];
  let best = next.notes[0];
  for (const x of next.notes) if (Math.abs(midiOf(x.pitch) - m) < Math.abs(midiOf(best.pitch) - m)) best = x;
  return best;
}

function prepareTrack(track: Track): TrackPrep {
  const prep: TrackPrep = {
    tieStop: new Set(),
    hpStart: new Map(),
    hpStop: new Map(),
    slideStop: new Set(),
    legatoStop: new Set(),
    slideStart: new Set(),
    wedgeStopAfter: new Map(),
    before: new Map(),
    after: new Map(),
  };
  const push = (m: Map<string, RunEvent[]>, id: string, e: RunEvent) => {
    const l = m.get(id);
    if (l) l.push(e);
    else m.set(id, [e]);
  };
  const beatIds = new Set<string>();
  for (const tb of track.bars) for (const v of tb.voices) for (const b of v) beatIds.add(b.id);
  const voices = Math.max(1, ...track.bars.map((tb) => tb.voices.length));
  for (let v = 0; v < voices; v++) {
    const flat = flatVoice(track, v);
    const main = flat.filter((f) => !f.beat.grace);
    for (let i = 0; i < main.length; i++) {
      const b = main[i].beat;
      const next = main[i + 1]?.beat;
      if (!isRestBeat(b)) {
        for (const n of b.notes) {
          if (n.tieToNext) {
            const d = destinationNote(n, next, true);
            if (d) prep.tieStop.add(d.id);
          }
          if (n.hammerPull) {
            const d = destinationNote(n, next, false);
            const type = d && midiOf(d.pitch) < midiOf(n.pitch) ? 'pull-off' : 'hammer-on';
            prep.hpStart.set(n.id, type);
            if (d) prep.hpStop.set(d.id, type);
          }
          if (n.slide === 'legato' || n.slide === 'shift') {
            const d = destinationNote(n, next, false);
            if (d) {
              prep.slideStart.add(n.id);
              prep.slideStop.add(d.id);
              if (n.slide === 'legato') prep.legatoStop.add(d.id);
            }
          }
        }
      }
      if (b.hairpin) {
        const end = b.hairpin.endBeatId && beatIds.has(b.hairpin.endBeatId) ? b.hairpin.endBeatId : b.id;
        const l = prep.wedgeStopAfter.get(end) ?? [];
        l.push(v + 1);
        prep.wedgeStopAfter.set(end, l);
      }
    }
    // Let-ring runs (non-grace beats) and octave-shift runs (all beats).
    let ringOpen: Beat | null = null;
    for (let i = 0; i < main.length; i++) {
      const b = main[i].beat;
      if (b.letRing && !ringOpen) {
        push(prep.before, b.id, { kind: 'letRing', start: true, voice: v });
        ringOpen = b;
      }
      if (ringOpen && b.letRing && !main[i + 1]?.beat.letRing) {
        push(prep.after, b.id, { kind: 'letRing', start: false, voice: v });
        ringOpen = null;
      }
    }
    let cur = 0;
    for (let i = 0; i < flat.length; i++) {
      const b = flat[i].beat;
      const s = b.octaveShift ?? 0;
      if (s !== cur) {
        if (s !== 0) push(prep.before, b.id, { kind: 'octave', start: true, voice: v, shift: s });
        cur = s;
      }
      const ns = flat[i + 1]?.beat.octaveShift ?? 0;
      if (cur !== 0 && ns !== cur) push(prep.after, b.id, { kind: 'octave', start: false, voice: v, shift: cur });
    }
  }
  return prep;
}

function writeClef(w: XmlWriter, clef: Clef | 'TAB', number?: number): void {
  const attrs = number ? { number } : undefined;
  w.open('clef', attrs);
  switch (clef) {
    case 'treble':
      w.leaf('sign', 'G').leaf('line', 2);
      break;
    case 'treble8vb':
      w.leaf('sign', 'G').leaf('line', 2).leaf('clef-octave-change', -1);
      break;
    case 'bass':
      w.leaf('sign', 'F').leaf('line', 4);
      break;
    case 'alto':
      w.leaf('sign', 'C').leaf('line', 3);
      break;
    case 'tenor':
      w.leaf('sign', 'C').leaf('line', 4);
      break;
    case 'percussion':
      w.leaf('sign', 'percussion');
      break;
    case 'TAB':
      w.leaf('sign', 'TAB').leaf('line', 5);
      break;
  }
  w.close('clef');
}

function writePitch(w: XmlWriter, p: Pitch, octaveDelta: number): void {
  w.open('pitch').leaf('step', p.step);
  if (p.alter) w.leaf('alter', p.alter);
  w.leaf('octave', p.octave + octaveDelta).close('pitch');
}

function fmtNum(x: number): string {
  return String(Math.round(x * 1000) / 1000);
}

function chordDisplay(name: string): { rootText?: string; bassText?: string } {
  const m = /^([A-Ga-g](?:bb|b|♭|𝄫|##|#|♯|x|𝄪)?)/u.exec(name.trim());
  const b = /\/([A-G](?:bb|b|♭|𝄫|##|#|♯|x|𝄪)?)$/u.exec(name.trim());
  return { rootText: m?.[1], bassText: b?.[1] };
}

function writeHarmony(w: XmlWriter, beat: Beat): void {
  const ch = beat.chord;
  if (!ch || !ch.name) return;
  const p = parseChordName(ch.name);
  w.open('harmony', { 'print-frame': 'no' });
  if (p.root) {
    const disp = chordDisplay(ch.name);
    const plain = stepAlterText(p.root.step, p.root.alter);
    w.open('root').leaf('root-step', p.root.step, disp.rootText && disp.rootText !== plain ? { text: disp.rootText } : undefined);
    if (p.root.alter) w.leaf('root-alter', p.root.alter);
    w.close('root');
    w.leaf('kind', p.kind, { text: p.suffix });
    if (p.bass) {
      const bplain = stepAlterText(p.bass.step, p.bass.alter);
      w.open('bass').leaf('bass-step', p.bass.step, disp.bassText && disp.bassText !== bplain ? { text: disp.bassText } : undefined);
      if (p.bass.alter) w.leaf('bass-alter', p.bass.alter);
      w.close('bass');
    }
  } else {
    w.open('root').leaf('root-step', 'C', { text: '' }).close('root');
    w.leaf('kind', p.kind === 'none' ? 'none' : 'other', { text: ch.name });
  }
  const dg = ch.diagram;
  if (dg && dg.frets.length) {
    const strings = dg.frets.length;
    const played = dg.frets.filter((f): f is number => f !== null && f !== undefined);
    const maxF = played.length ? Math.max(...played) : 0;
    const base = Math.max(1, dg.baseFret || 1);
    w.open('frame');
    w.leaf('frame-strings', strings);
    w.leaf('frame-frets', Math.max(4, maxF - base + 1));
    if (base > 1) w.leaf('first-fret', base);
    dg.frets.forEach((f, i) => {
      if (f === null || f === undefined) return;
      w.open('frame-note').leaf('string', i + 1).leaf('fret', f);
      const fg = dg.fingers?.[i];
      if (fg !== null && fg !== undefined) w.leaf('fingering', fg);
      w.close('frame-note');
    });
    w.close('frame');
  }
  w.close('harmony');
}

interface BeamInfo {
  levels: string[];
}

/** Simple beaming within the time signature's beat groups. */
function computeBeams(beats: Beat[], groups: number[]): Map<string, BeamInfo> {
  const out = new Map<string, BeamInfo>();
  const bounds: number[] = [];
  let acc = 0;
  for (const g of groups) {
    acc += g;
    bounds.push(acc);
  }
  const groupOf = (t: number) => {
    for (let i = 0; i < bounds.length; i++) if (t < bounds[i] - 1e-6) return i;
    return bounds.length;
  };
  let run: { beat: Beat; base: number }[] = [];
  let runGroup = -1;
  const flush = () => {
    if (run.length >= 2) {
      const maxLevel = Math.max(...run.map((r) => Math.log2(r.base) - 2));
      for (let j = 0; j < run.length; j++) {
        const levels: string[] = [];
        levels.push(j === 0 ? 'begin' : j === run.length - 1 ? 'end' : 'continue');
        for (let L = 2; L <= maxLevel; L++) {
          const has = (k: number) => k >= 0 && k < run.length && Math.log2(run[k].base) - 2 >= L;
          if (!has(j)) break;
          const pv = has(j - 1);
          const nx = has(j + 1);
          levels.push(pv && nx ? 'continue' : pv ? 'end' : nx ? 'begin' : j === 0 ? 'forward hook' : 'backward hook');
        }
        out.set(run[j].beat.id, { levels });
      }
    }
    run = [];
    runGroup = -1;
  };
  let t = 0;
  for (const b of beats) {
    if (b.grace) continue;
    const len = durTicks(b.duration);
    const g = groupOf(t);
    const beamable = !isRestBeat(b) && b.duration.base >= 8;
    if (!beamable || b.beamBreak === 'break' || (g !== runGroup && b.beamBreak !== 'join') || (len > 0 && groupOf(t + len - 1) !== g && b.beamBreak !== 'join')) {
      flush();
    }
    if (beamable) {
      if (!run.length) runGroup = g;
      run.push({ beat: b, base: b.duration.base });
    }
    t += len;
  }
  flush();
  return out;
}

/** Serialize a score as an uncompressed MusicXML 4.0 partwise document. */
export function exportMusicXml(score: Score): string {
  const w = new XmlWriter();
  w.raw('<?xml version="1.0" encoding="UTF-8" standalone="no"?>');
  w.raw('<!DOCTYPE score-partwise PUBLIC "-//Recordare//DTD MusicXML 4.0 Partwise//EN" "http://www.musicxml.org/dtds/partwise.dtd">');
  w.open('score-partwise', { version: '4.0' });
  const meta = score.meta;
  const title = meta.title ?? '';
  w.open('work').leaf('work-title', title).close('work');
  if (title) w.leaf('movement-title', title);
  w.open('identification');
  if (meta.composer) w.leaf('creator', meta.composer, { type: 'composer' });
  if (meta.artist) w.leaf('creator', meta.artist, { type: 'artist' });
  if (meta.arranger) w.leaf('creator', meta.arranger, { type: 'arranger' });
  if (meta.copyright) w.leaf('rights', meta.copyright);
  w.open('encoding');
  w.leaf('software', 'SimpleNotation');
  w.leaf('encoding-date', new Date().toISOString().slice(0, 10));
  w.leaf('supports', undefined, { element: 'accidental', type: 'yes' });
  w.leaf('supports', undefined, { element: 'beam', type: 'yes' });
  w.leaf('supports', undefined, { element: 'stem', type: 'no' });
  w.leaf('supports', undefined, { element: 'print', attribute: 'new-system', type: 'yes', value: 'yes' });
  w.close('encoding');
  w.close('identification');
  if (title) {
    w.open('credit', { page: 1 }).leaf('credit-type', 'title').leaf('credit-words', title, { justify: 'center', valign: 'top', 'font-size': 22 }).close('credit');
  }
  if (meta.subtitle) {
    w.open('credit', { page: 1 }).leaf('credit-type', 'subtitle').leaf('credit-words', meta.subtitle, { justify: 'center', valign: 'top', 'font-size': 16 }).close('credit');
  }

  // ---- part list
  w.open('part-list');
  const channels = midiChannels(score).map((c) => c + 1);
  score.tracks.forEach((t, ti) => {
    const id = `P${ti + 1}`;
    w.open('score-part', { id });
    w.leaf('part-name', t.name || `Track ${ti + 1}`);
    if (t.shortName) w.leaf('part-abbreviation', t.shortName);
    const def = instrumentDef(t.instrument);
    w.open('score-instrument', { id: `${id}-I1` });
    w.leaf('instrument-name', def.id === t.instrument ? def.name : t.instrument);
    const xs = xmlSoundOf(t.sound);
    if (xs) w.leaf('instrument-sound', xs);
    w.close('score-instrument');
    w.open('midi-instrument', { id: `${id}-I1` });
    w.leaf('midi-channel', channels[ti]);
    w.leaf('midi-program', gmProgramOf(t.sound) + 1);
    w.leaf('volume', fmtNum(Math.max(0, Math.min(1, t.volume ?? 0.8)) * 100));
    w.leaf('pan', fmtNum(Math.max(-1, Math.min(1, t.pan ?? 0)) * 90));
    w.close('midi-instrument');
    w.close('score-part');
  });
  w.close('part-list');

  // ---- parts
  score.tracks.forEach((track, ti) => exportPart(w, score, track, ti));
  w.close('score-partwise');
  return w.toString();
}

function endingGroups(bars: BarInfo[]): { start: Set<number>; stop: Set<number> } {
  const start = new Set<number>();
  const stop = new Set<number>();
  const key = (b: BarInfo | undefined) => (b?.endings && b.endings.length ? b.endings.join(',') : '');
  for (let i = 0; i < bars.length; i++) {
    const k = key(bars[i]);
    if (!k) continue;
    if (key(bars[i - 1]) !== k || bars[i].repeatStart) start.add(i);
    if (key(bars[i + 1]) !== k || bars[i].repeatEnd || bars[i + 1]?.repeatStart) stop.add(i);
  }
  return { start, stop };
}

function exportPart(w: XmlWriter, score: Score, track: Track, ti: number): void {
  w.open('part', { id: `P${ti + 1}` });
  const fretted = isFretted(track);
  const tabOnly = fretted && !track.showStaff;
  const percussion = track.clef === 'percussion';
  const xmlShift = tabOnly ? track.writtenToSounding : clefXmlShift(track.clef);
  const transpose = tabOnly ? 0 : track.writtenToSounding - xmlShift;
  const prep = prepareTrack(track);
  const { start: endStart, stop: endStop } = endingGroups(score.bars);
  let measureNo = score.bars[0]?.pickup ? 0 : 1;
  let curClef: Clef = track.clef;
  const slurOpen = new Set<number>();

  // Lyric syllable state per voice/verse.
  const inWord = new Map<string, boolean>();

  for (let bi = 0; bi < score.bars.length; bi++) {
    const info = score.bars[bi];
    const ts = timeSigAt(score, bi);
    const cap = barCapacity(ts);
    const tb = track.bars[bi] ?? { voices: [[]] };
    const repeatCount = info.multiRest && info.multiRest > 1 ? info.multiRest : 1;

    w.open('measure', { number: measureNo, implicit: info.pickup ? 'yes' : undefined });
    measureNo++;
    if (bi > 0 && score.bars[bi - 1].lineBreak === 'force') w.leaf('print', undefined, { 'new-system': 'yes' });

    // Left barline.
    if (info.repeatStart || endStart.has(bi)) {
      w.open('barline', { location: 'left' });
      if (info.repeatStart) w.leaf('bar-style', 'heavy-light');
      if (endStart.has(bi)) {
        const nums = info.endings!.join(', ');
        w.leaf('ending', info.endings!.map((n) => `${n}.`).join(' '), { number: nums, type: 'start' });
      }
      if (info.repeatStart) w.leaf('repeat', undefined, { direction: 'forward' });
      w.close('barline');
    }

    // Attributes.
    const clefChange = bi > 0 ? track.clefChanges?.[bi] : undefined;
    const needAttrs = bi === 0 || !!info.keySig || !!info.timeSig || (!!clefChange && clefChange !== curClef && !tabOnly) || repeatCount > 1;
    if (needAttrs) {
      w.open('attributes');
      if (bi === 0) w.leaf('divisions', DIVISIONS);
      if (bi === 0 || info.keySig) {
        const ks: KeySignature = percussion ? { fifths: 0, mode: 'major' } : writtenKey(info.keySig ?? keySigAt(score, bi), track);
        w.open('key').leaf('fifths', ks.fifths).leaf('mode', ks.mode).close('key');
      }
      if (bi === 0 || info.timeSig) {
        const t = info.timeSig ?? ts;
        w.open('time', t.symbol ? { symbol: t.symbol } : undefined).leaf('beats', t.num).leaf('beat-type', t.den).close('time');
      }
      if (bi === 0) writeClef(w, tabOnly ? 'TAB' : track.clef);
      else if (clefChange && clefChange !== curClef && !tabOnly) {
        writeClef(w, clefChange);
        curClef = clefChange;
      }
      if (bi === 0 && fretted && track.tuning) {
        w.open('staff-details');
        if (tabOnly) w.leaf('staff-lines', track.tuning.length);
        // MusicXML numbers tuning lines from the bottom (lowest string = line 1).
        const low2high = [...track.tuning].reverse();
        low2high.forEach((m, i) => {
          const p = spellMidi(m, 0);
          w.open('staff-tuning', { line: i + 1 }).leaf('tuning-step', p.step);
          if (p.alter) w.leaf('tuning-alter', p.alter);
          w.leaf('tuning-octave', p.octave).close('staff-tuning');
        });
        if (track.capo) w.leaf('capo', track.capo);
        w.close('staff-details');
      }
      if (bi === 0 && transpose !== 0) {
        const oct = Math.trunc(transpose / 12);
        const chrom = transpose - 12 * oct;
        const diat = Math.sign(chrom) * DIATONIC_FOR_SEMIS[Math.abs(chrom)];
        w.open('transpose').leaf('diatonic', diat).leaf('chromatic', chrom);
        if (oct) w.leaf('octave-change', oct);
        w.close('transpose');
      }
      if (repeatCount > 1) w.open('measure-style').leaf('multiple-rest', repeatCount).close('measure-style');
      w.close('attributes');
    }

    // Bar-level directions at the start (first part only).
    const tempoInline = new Map<number, typeof score.tempos>();
    if (ti === 0) {
      if (info.section && (info.section.letter || info.section.name)) {
        w.open('direction', { placement: 'above' });
        w.open('direction-type').leaf('rehearsal', info.section.letter || info.section.name!).close('direction-type');
        if (info.section.letter && info.section.name) w.open('direction-type').leaf('words', info.section.name).close('direction-type');
        w.close('direction');
      }
      if (info.direction === 'Segno') {
        w.open('direction', { placement: 'above' }).open('direction-type').leaf('segno').close('direction-type').leaf('sound', undefined, { segno: 'segno' }).close('direction');
      }
      if (info.direction === 'Coda') {
        w.open('direction', { placement: 'above' }).open('direction-type').leaf('coda').close('direction-type').leaf('sound', undefined, { coda: 'coda' }).close('direction');
      }
      if (info.tripletFeel) {
        w.open('direction', { placement: 'above' });
        w.open('direction-type').leaf('words', info.tripletFeel === 'none' ? 'Straight' : info.tripletFeel === '8th' ? 'Swing' : 'Swing 16ths').close('direction-type');
        w.open('sound');
        if (info.tripletFeel === 'none') w.open('swing').leaf('straight').close('swing');
        else w.open('swing').leaf('first', 2).leaf('second', 1).leaf('swing-type', info.tripletFeel === '8th' ? 'eighth' : '16th').close('swing');
        w.close('sound');
        w.close('direction');
      }
      const v0 = tb.voices[0] ?? [];
      const onsets = new Set<number>();
      let t = 0;
      for (const b of v0) {
        onsets.add(Math.round(t));
        if (!b.grace) t += durTicks(b.duration);
      }
      for (const tm of score.tempos) {
        if (tm.bar !== bi) continue;
        const off = Math.round(tm.beatOffsetTicks);
        if (off > 0 && onsets.has(off)) {
          const l = tempoInline.get(off) ?? [];
          l.push(tm);
          tempoInline.set(off, l);
        } else writeTempo(w, tm, off);
      }
    }

    // Accidentals for the bar (all voices, time order).
    const accMarks = barAccidentals(score, track, bi);

    // Voices.
    const groups = beatGroups(ts);
    let cursor = 0; // integer divisions written so far in this pass
    const voiceCount = tb.voices.length;
    let wroteVoice = false;
    for (let v = 0; v < voiceCount; v++) {
      const beats = tb.voices[v] ?? [];
      if (v > 0 && beats.length === 0) continue;
      if (wroteVoice && cursor > 0) {
        w.open('backup').leaf('duration', cursor).close('backup');
        cursor = 0;
      }
      wroteVoice = true;
      if (beats.length === 0) {
        // Empty voice 0: a whole-measure rest.
        w.open('note').leaf('rest', undefined, { measure: 'yes' }).leaf('duration', Math.round(cap * repeatCount)).leaf('voice', v + 1).close('note');
        cursor += Math.round(cap * repeatCount);
        continue;
      }
      const beams = computeBeams(beats, groups);
      let exact = 0;
      const tupletMarks = tupletBrackets(beats);
      for (let k = 0; k < beats.length; k++) {
        const beat = beats[k];
        const onset = Math.round(exact);
        // Directions before the beat.
        if (ti === 0 && v === 0 && !beat.grace) {
          const tl = tempoInline.get(onset);
          if (tl) {
            for (const tm of tl) writeTempo(w, tm, 0);
            tempoInline.delete(onset);
          }
        }
        for (const ev of prep.before.get(beat.id) ?? []) writeRunEvent(w, ev);
        writeBeatDirections(w, beat, v);
        writeHarmony(w, beat);
        const len = beat.grace ? 0 : durTicks(beat.duration);
        const durDiv = beat.grace ? 0 : Math.round(exact + len) - Math.round(exact);
        const isMeasureRest = isRestBeat(beat) && beats.length === 1 && Math.abs(len - cap) < 1e-6 && !info.pickup;
        const notes: (Note | null)[] = isRestBeat(beat) ? [null] : beat.notes;
        notes.forEach((note, ni) => {
          writeNote(w, {
            score,
            track,
            prep,
            beat,
            note,
            first: ni === 0,
            voice: v,
            durDiv: isMeasureRest ? Math.round(cap * repeatCount) : durDiv,
            measureRest: isMeasureRest,
            xmlShift,
            percussion,
            tabOnly,
            beam: ni === 0 ? beams.get(beat.id) : undefined,
            tuplet: ni === 0 ? tupletMarks.get(beat.id) : undefined,
            accidental: note ? accMarks.get(note.id) : undefined,
            slurOpen,
            inWord,
          });
        });
        if (!beat.grace) {
          exact += isMeasureRest ? cap * repeatCount : len;
          cursor += isMeasureRest ? Math.round(cap * repeatCount) : durDiv;
        }
        for (const num of prep.wedgeStopAfter.get(beat.id) ?? []) {
          w.open('direction', { placement: 'below' }).open('direction-type').leaf('wedge', undefined, { type: 'stop', number: num }).close('direction-type').leaf('voice', v + 1).close('direction');
        }
        for (const ev of prep.after.get(beat.id) ?? []) writeRunEvent(w, ev);
      }
      // Remaining inline tempos (positions not matching a beat) are written at the end with an offset.
      if (ti === 0 && v === 0) {
        for (const [off, tl] of tempoInline) for (const tm of tl) writeTempo(w, tm, off - cursor);
        tempoInline.clear();
        // Bar-level jumps at the end of voice 0.
        const d = info.direction;
        if (d && d !== 'Segno' && d !== 'Coda') {
          const sound: XmlAttrs =
            d === 'Fine'
              ? { fine: 'yes' }
              : d === 'ToCoda'
                ? { tocoda: 'coda' }
                : d.startsWith('DC')
                  ? { dacapo: 'yes' }
                  : { dalsegno: 'segno' };
          w.open('direction', { placement: 'above' }).open('direction-type').leaf('words', DIRECTION_WORDS[d], { 'font-style': 'italic' }).close('direction-type').leaf('sound', undefined, sound).close('direction');
        }
      }
    }

    // Right barline.
    const style = info.repeatEnd ? 'light-heavy' : info.barline === 'double' ? 'light-light' : info.barline === 'dashed' ? 'dashed' : info.barline === 'final' ? 'light-heavy' : undefined;
    if (style || info.repeatEnd || endStop.has(bi)) {
      w.open('barline', { location: 'right' });
      if (style) w.leaf('bar-style', style);
      if (endStop.has(bi)) {
        w.leaf('ending', undefined, { number: info.endings!.join(', '), type: info.repeatEnd ? 'stop' : 'discontinue' });
      }
      if (info.repeatEnd) w.leaf('repeat', undefined, { direction: 'backward', times: info.repeatEnd !== 2 ? info.repeatEnd : undefined });
      w.close('barline');
    }
    w.close('measure');

    // Expanded multi-measure rest: the remaining measures are empty.
    for (let r = 1; r < repeatCount; r++) {
      w.open('measure', { number: measureNo });
      measureNo++;
      w.open('note').leaf('rest', undefined, { measure: 'yes' }).leaf('duration', Math.round(cap)).leaf('voice', 1).close('note');
      w.close('measure');
    }
  }
  w.close('part');
}

function writeTempo(w: XmlWriter, tm: Score['tempos'][number], offset: number): void {
  w.open('direction', { placement: 'above' });
  if (tm.text) w.open('direction-type').leaf('words', tm.text, { 'font-weight': 'bold' }).close('direction-type');
  w.open('direction-type').open('metronome', { parentheses: 'no' }).leaf('beat-unit', TYPE_NAMES[tm.beatUnit] ?? 'quarter');
  if (tm.dotted) w.leaf('beat-unit-dot');
  w.leaf('per-minute', fmtNum(tm.bpm)).close('metronome').close('direction-type');
  if (offset) w.leaf('offset', offset, { sound: 'yes' });
  w.leaf('sound', undefined, { tempo: fmtNum(markQpm(tm)) });
  w.close('direction');
}

function writeRunEvent(w: XmlWriter, ev: RunEvent): void {
  if (ev.kind === 'letRing') {
    w.open('direction', { placement: 'below' });
    if (ev.start) w.open('direction-type').leaf('words', 'let ring', { 'font-style': 'italic' }).close('direction-type');
    w.open('direction-type').leaf('dashes', undefined, { type: ev.start ? 'start' : 'stop', number: ev.voice + 1 }).close('direction-type');
    w.leaf('voice', ev.voice + 1);
    w.close('direction');
  } else {
    const size = Math.abs(ev.shift) === 15 ? 15 : 8;
    const type = ev.start ? (ev.shift > 0 ? 'down' : 'up') : 'stop';
    w.open('direction', { placement: ev.shift > 0 ? 'above' : 'below' });
    w.open('direction-type').leaf('octave-shift', undefined, { type, size, number: ev.voice + 1 }).close('direction-type');
    w.leaf('voice', ev.voice + 1);
    w.close('direction');
  }
}

function writeBeatDirections(w: XmlWriter, beat: Beat, v: number): void {
  const simple = (placement: string, body: () => void) => {
    w.open('direction', { placement });
    w.open('direction-type');
    body();
    w.close('direction-type');
    w.leaf('voice', v + 1);
    w.close('direction');
  };
  if (beat.text?.above) simple('above', () => w.leaf('words', beat.text!.above!));
  if (beat.text?.below) simple('below', () => w.leaf('words', beat.text!.below!));
  if (beat.dynamics) simple('below', () => w.open('dynamics').leaf(beat.dynamics!).close('dynamics'));
  if (beat.hairpin) simple('below', () => w.leaf('wedge', undefined, { type: beat.hairpin!.type === 'cresc' ? 'crescendo' : 'diminuendo', number: v + 1 }));
  if (beat.pedal) simple('below', () => w.leaf('pedal', undefined, { type: beat.pedal === 'down' ? 'start' : 'stop', line: 'no', sign: 'yes' }));
}

/** Tuplet bracket start/stop per beat id for a voice. */
function tupletBrackets(beats: Beat[]): Map<string, 'start' | 'stop' | 'both'> {
  const out = new Map<string, 'start' | 'stop' | 'both'>();
  let i = 0;
  while (i < beats.length) {
    const b = beats[i];
    if (b.grace || !b.duration.tuplet) {
      i++;
      continue;
    }
    const { actual, normal } = b.duration.tuplet;
    let plain = 0;
    let maxBase = 0;
    let j = i;
    let last = i;
    for (; j < beats.length; j++) {
      const x = beats[j];
      if (x.grace) continue;
      const tp = x.duration.tuplet;
      if (!tp || tp.actual !== actual || tp.normal !== normal) break;
      maxBase = Math.max(maxBase, x.duration.base);
      plain += (durTicks(x.duration) * actual) / normal;
      last = j;
      const unit = (PPQ * 4) / maxBase;
      if (Math.abs(plain / (actual * unit) - Math.round(plain / (actual * unit))) < 1e-6 && plain > 0) {
        j++;
        break;
      }
    }
    if (last === i) out.set(b.id, 'both');
    else {
      out.set(b.id, 'start');
      out.set(beats[last].id, 'stop');
    }
    i = Math.max(j, i + 1);
  }
  return out;
}

function barAccidentals(score: Score, track: Track, bi: number): Map<string, AccidentalMark> {
  const out = new Map<string, AccidentalMark>();
  if (track.clef === 'percussion') return out;
  const tb = track.bars[bi];
  if (!tb) return out;
  const items: { onset: number; voice: number; note: Note; tied: boolean }[] = [];
  // Tie destinations: notes whose predecessor in the voice ties into them.
  tb.voices.forEach((beats, v) => {
    let t = 0;
    let prev: Beat | undefined = bi > 0 ? [...(track.bars[bi - 1]?.voices[v] ?? [])].reverse().find((b) => !b.grace) : undefined;
    for (const b of beats) {
      if (!isRestBeat(b)) {
        for (const n of b.notes) {
          const tied = !b.grace && !!prev && !isRestBeat(prev) && prev.notes.some((p) => p.tieToNext && midiOf(p.pitch) === midiOf(n.pitch));
          items.push({ onset: t, voice: v, note: n, tied });
        }
      }
      if (!b.grace) {
        t += durTicks(b.duration);
        prev = b;
      }
    }
  });
  items.sort((a, b) => a.onset - b.onset || a.voice - b.voice);
  const fifths = writtenKey(keySigAt(score, bi), track).fifths;
  const inputs: AccidentalInput[] = items.map((it) => ({ pitch: it.note.pitch, tiedFromPrev: it.tied, display: it.note.accidentalDisplay }));
  const marks = computeAccidentals(inputs, fifths);
  items.forEach((it, i) => out.set(it.note.id, marks[i]));
  return out;
}

interface NoteCtx {
  score: Score;
  track: Track;
  prep: TrackPrep;
  beat: Beat;
  note: Note | null;
  first: boolean;
  voice: number;
  durDiv: number;
  measureRest: boolean;
  xmlShift: number;
  percussion: boolean;
  tabOnly: boolean;
  beam?: BeamInfo;
  tuplet?: 'start' | 'stop' | 'both';
  accidental?: AccidentalMark;
  slurOpen: Set<number>;
  inWord: Map<string, boolean>;
}

const HEAD_XML: Partial<Record<string, string>> = { x: 'x', diamond: 'diamond', slash: 'slash', circleX: 'circle-x' };

function writeNote(w: XmlWriter, c: NoteCtx): void {
  const { beat, note, voice: v, track } = c;
  const attrs: XmlAttrs = {};
  if (beat.hidden || note?.hidden) attrs['print-object'] = 'no';
  const color = note?.color ?? beat.color;
  if (color && /^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(color)) attrs.color = color.toUpperCase();
  w.open('note', attrs);
  if (beat.grace) w.leaf('grace', undefined, beat.grace === 'acciaccatura' ? { slash: 'yes' } : undefined);
  if (!c.first) w.leaf('chord');
  const octShift = beat.octaveShift ? (Math.abs(beat.octaveShift) === 15 ? 2 : 1) * Math.sign(beat.octaveShift) : 0;
  if (!note) {
    w.leaf('rest', undefined, c.measureRest ? { measure: 'yes' } : undefined);
  } else if (c.percussion) {
    w.open('unpitched').leaf('display-step', note.pitch.step).leaf('display-octave', note.pitch.octave).close('unpitched');
  } else if (c.tabOnly) {
    const sounding = spellMidi(midiOf(note.pitch) + track.writtenToSounding, 0);
    // Keep the written spelling where possible.
    const shifted: Pitch = { ...note.pitch, octave: note.pitch.octave + Math.round(track.writtenToSounding / 12) };
    writePitch(w, midiOf(shifted) === midiOf(sounding) ? shifted : sounding, octShift);
  } else {
    writePitch(w, note.pitch, c.xmlShift / 12 + octShift);
  }
  if (!beat.grace) w.leaf('duration', c.durDiv);
  if (note && c.prep.tieStop.has(note.id)) w.leaf('tie', undefined, { type: 'stop' });
  if (note?.tieToNext) w.leaf('tie', undefined, { type: 'start' });
  w.leaf('voice', v + 1);
  w.leaf('type', TYPE_NAMES[beat.duration.base], beat.small ? { size: 'cue' } : undefined);
  for (let d = 0; d < beat.duration.dots; d++) w.leaf('dot');
  if (note && c.accidental) {
    const caution = c.accidental.cautionary || note.accidentalDisplay === 'cautionary';
    w.leaf('accidental', ALTER_NAMES[c.accidental.alter] ?? 'natural', caution ? { cautionary: 'yes' } : undefined);
  }
  const tp = beat.duration.tuplet;
  if (tp) w.open('time-modification').leaf('actual-notes', tp.actual).leaf('normal-notes', tp.normal).close('time-modification');
  if (c.first && beat.stem && beat.stem !== 'auto') w.leaf('stem', beat.stem === 'hidden' ? 'none' : beat.stem);
  if (note) {
    const head = note.dead ? 'x' : note.head ? HEAD_XML[note.head] : undefined;
    const ghost = note.ghost || note.head === 'ghost';
    if (head || ghost) w.leaf('notehead', head ?? 'normal', ghost ? { parentheses: 'yes' } : undefined);
  }
  if (c.beam) c.beam.levels.forEach((lv, i) => w.leaf('beam', lv, { number: i + 1 }));
  writeNotations(w, c);
  if (note && c.first && beat.lyrics) {
    beat.lyrics.forEach((text, i) => {
      if (!text) return;
      const key = `${v}:${i}`;
      const open = c.inWord.get(key) ?? false;
      let t = text;
      let syl: string;
      if (t.endsWith('-') && t.length > 1) {
        t = t.slice(0, -1);
        syl = open ? 'middle' : 'begin';
        c.inWord.set(key, true);
      } else {
        syl = open ? 'end' : 'single';
        c.inWord.set(key, false);
      }
      w.open('lyric', { number: i + 1 }).leaf('syllabic', syl).leaf('text', t).close('lyric');
    });
  }
  if (note && beat.palmMute) w.open('play').leaf('mute', 'palm').close('play');
  w.close('note');
}

function writeNotations(w: XmlWriter, c: NoteCtx): void {
  const { beat, note, first, voice: v, prep } = c;
  const parts: Array<() => void> = [];
  if (note) {
    if (prep.tieStop.has(note.id)) parts.push(() => w.leaf('tied', undefined, { type: 'stop' }));
    if (note.tieToNext) parts.push(() => w.leaf('tied', undefined, { type: 'start' }));
    if (beat.letRing) parts.push(() => w.leaf('tied', undefined, { type: 'let-ring' }));
    const slurNo = 2 * v + 1;
    if (note.slurEnd) parts.push(() => w.leaf('slur', undefined, { type: 'stop', number: slurNo }));
    if (note.slurStart) {
      const placement = note.slurSide === 'above' || note.slurSide === 'below' ? note.slurSide : undefined;
      parts.push(() => w.leaf('slur', undefined, { type: 'start', number: slurNo, placement }));
    }
    if (prep.legatoStop.has(note.id)) parts.push(() => w.leaf('slur', undefined, { type: 'stop', number: slurNo + 1 }));
    if (note.slide === 'legato' && prep.slideStart.has(note.id)) parts.push(() => w.leaf('slur', undefined, { type: 'start', number: slurNo + 1 }));
    if (prep.slideStop.has(note.id)) parts.push(() => w.leaf('slide', undefined, { type: 'stop', 'line-type': 'solid', number: v + 1 }));
    if ((note.slide === 'legato' || note.slide === 'shift') && prep.slideStart.has(note.id))
      parts.push(() => w.leaf('slide', undefined, { type: 'start', 'line-type': 'solid', number: v + 1 }));
  }
  if (first && c.tuplet) {
    if (c.tuplet === 'start' || c.tuplet === 'both') parts.push(() => w.leaf('tuplet', undefined, { type: 'start', bracket: 'yes', number: 1 }));
    if (c.tuplet === 'stop' || c.tuplet === 'both') parts.push(() => w.leaf('tuplet', undefined, { type: 'stop', number: 1 }));
  }
  // Ornaments.
  const orn: Array<() => void> = [];
  if (first && beat.ornament === 'trill') orn.push(() => w.leaf('trill-mark'));
  if (first && beat.ornament === 'mordent') orn.push(() => w.leaf('mordent'));
  if (first && beat.ornament === 'turn') orn.push(() => w.leaf('turn'));
  if (note?.vibrato) {
    orn.push(() => w.leaf('wavy-line', undefined, { type: 'start', number: 2 }));
    orn.push(() => w.leaf('wavy-line', undefined, { type: 'stop', number: 2 }));
  }
  if (first && beat.tremolo) orn.push(() => w.leaf('tremolo', beat.tremolo, { type: 'single' }));
  if (orn.length) parts.push(() => {
    w.open('ornaments');
    orn.forEach((f) => f());
    w.close('ornaments');
  });
  // Technical.
  const tech: Array<() => void> = [];
  if (first && beat.pick) tech.push(() => w.leaf(beat.pick === 'down' ? 'down-bow' : 'up-bow'));
  if (first && beat.mute) {
    const m = beat.mute;
    if (m === 'open') tech.push(() => w.leaf('open'));
    else if (m === 'closed') tech.push(() => w.leaf('stopped'));
    else if (m === 'halfOpen1') tech.push(() => w.leaf('half-muted'));
    else tech.push(() => w.open('harmon-mute').leaf('harmon-closed', 'half').close('harmon-mute'));
  }
  if (note) {
    if (note.harmonic) tech.push(() => w.open('harmonic').leaf(note.harmonic === 'artificial' ? 'artificial' : 'natural').close('harmonic'));
    if (note.fingering) tech.push(() => w.leaf('fingering', note.fingering));
    if (note.rhFingering) tech.push(() => w.leaf('pluck', note.rhFingering));
    if (note.string !== undefined) tech.push(() => w.leaf('string', note.string, note.stringCircle ? { placement: 'above' } : undefined));
    if (note.fret !== undefined) tech.push(() => w.leaf('fret', note.fret));
    const hpStop = prep.hpStop.get(note.id);
    if (hpStop) tech.push(() => w.leaf(hpStop, undefined, { type: 'stop', number: v + 1 }));
    const hpStart = prep.hpStart.get(note.id);
    if (hpStart) tech.push(() => w.leaf(hpStart, hpStart === 'hammer-on' ? 'H' : 'P', { type: 'start', number: v + 1 }));
    if (note.bend && note.bend.points.length) {
      const pts = [...note.bend.points].sort((a, b) => a.t - b.t);
      if (pts[0].semis !== 0) {
        tech.push(() => w.open('bend', { 'first-beat': 0, 'last-beat': 0 }).leaf('bend-alter', fmtNum(pts[0].semis)).leaf('pre-bend').close('bend'));
      }
      for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1];
        const b = pts[i];
        const delta = b.semis - a.semis;
        if (Math.abs(delta) < 1e-9) continue;
        const attrs = { 'first-beat': fmtNum(a.t * 100), 'last-beat': fmtNum(b.t * 100) };
        tech.push(() => {
          w.open('bend', attrs).leaf('bend-alter', fmtNum(delta));
          if (delta < 0) w.leaf('release');
          w.close('bend');
        });
      }
    }
    if (note.tap) tech.push(() => w.leaf('tap'));
    if (note.leftHandTap) tech.push(() => w.leaf('tap', undefined, { hand: 'left' }));
    if (beat.tap) tech.push(() => w.leaf('tap', undefined, { hand: 'right' }));
    if (first && beat.slap) tech.push(() => w.leaf('other-technical', 'slap'));
    if (first && beat.pop) tech.push(() => w.leaf('other-technical', 'pop'));
  }
  if (tech.length) parts.push(() => {
    w.open('technical');
    tech.forEach((f) => f());
    w.close('technical');
  });
  // Articulations.
  const art: Array<() => void> = [];
  const arts = first ? beat.articulations ?? [] : [];
  for (const a of arts) {
    if (a === 'staccato') art.push(() => w.leaf('staccato'));
    else if (a === 'staccatissimo') art.push(() => w.leaf('staccatissimo'));
    else if (a === 'accent') art.push(() => w.leaf('accent'));
    else if (a === 'marcato') art.push(() => w.leaf('strong-accent'));
    else if (a === 'tenuto') art.push(() => w.leaf('tenuto'));
  }
  if (first && beat.jazz) {
    const el = beat.jazz === 'fall' ? 'falloff' : beat.jazz;
    art.push(() => w.leaf(el, undefined, { 'line-shape': 'curved' }));
  }
  if (note?.slide && note.slide !== 'legato' && note.slide !== 'shift') {
    const el = note.slide === 'inBelow' ? 'scoop' : note.slide === 'inAbove' ? 'plop' : note.slide === 'outUp' ? 'doit' : 'falloff';
    art.push(() => w.leaf(el, undefined, { 'line-shape': 'straight' }));
  }
  if (first && beat.breath) art.push(() => w.leaf('breath-mark'));
  if (art.length) parts.push(() => {
    w.open('articulations');
    art.forEach((f) => f());
    w.close('articulations');
  });
  if (first && arts.includes('fermata')) parts.push(() => w.leaf('fermata', undefined, { type: 'upright' }));
  const arp = beat.arpeggio ?? beat.strum ?? beat.brush;
  if (note && arp) parts.push(() => w.leaf('arpeggiate', undefined, { direction: arp, number: 1 }));
  if (!parts.length) return;
  w.open('notations');
  parts.forEach((f) => f());
  w.close('notations');
}
